import { Children, type FC, isValidElement, type ReactElement, type ReactNode, type RefObject } from 'react';

import { getPayloadFromElement, useDataTableContext } from './contexts/DataTableContext';
import type { ReactElementWithPayloadProp } from './types';
import { cn } from '../../../utils/cn';

type Props = {
    children: ReactNode | Array<ReactNode>;
    className?: string;
    tbodyRef?: RefObject<HTMLTableSectionElement | null>;
};

/**
 * Check if an element has a payload prop, either directly or on its single child.
 * This allows wrapping DataTableRow with components like ContextMenu.
 */
const hasPayload = (element: ReactNode): element is ReactElement =>
    isValidElement(element) && getPayloadFromElement(element) !== undefined;

export const DataTableAdvanced: FC<Props> = ({ children, className, tbodyRef }) => {
    const { sortTableRows } = useDataTableContext();

    // Flatten children array to handle React fragments and arrays
    const flattenedChildren = Children.toArray(children);

    const head = flattenedChildren.filter(
        (child) =>
            child &&
            typeof child === 'object' &&
            'props' in child &&
            child.props &&
            typeof child.props === 'object' &&
            'name' in child.props &&
            'field' in child.props,
    );

    const body = sortTableRows(flattenedChildren.filter(hasPayload) as ReactElementWithPayloadProp[]);

    return (
        <div
            className={cn(
                'bg-white border border-solid border-grey-outline-light rounded-md overflow-hidden',
                className,
            )}
        >
            <table className="w-full table-auto">
                <thead>
                    <tr>{head}</tr>
                </thead>
                <tbody className="overflow-y-scroll" {...(tbodyRef ? { ref: tbodyRef } : {})}>
                    {body}
                </tbody>
            </table>
        </div>
    );
};
